import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api, ApiError, fieldIssues } from '../api';
import { can, type Me } from '../auth/me';
import { formatPln } from '../catalog/money';
import { RowMenu, type RowAction } from '../ui/RowMenu';
import { monthLabel } from './format';
import { FINANCE_KEY, FIXED_RESOURCE, type FixedCost, type Occurrence, type PaymentStatus, type UnknownReason } from './types';

/** Owner 2026-10-24 01:18: each period of a fixed cost is paid, unpaid or unknown — from the transfers, or set by hand. */
export const PAYMENT_LABELS: Record<PaymentStatus, string> = { oplacone: 'opłacone', nieoplacone: 'nieopłacone', nieznany: 'nieznany' };
const REASONS: Record<UnknownReason, string> = {
  'brak-wskazowek': 'brak wskazówek do dopasowania przelewów',
  'brak-wyciagu': 'wyciągi nie obejmują jeszcze tego miesiąca',
  'przed-terminem': 'termin jeszcze nie minął',
};
const dayLabel = (day: string) => `${Number(day.slice(8, 10))}.${day.slice(5, 7)}.${day.slice(0, 4)}`;

/** What stands behind a period's status, in words: the paying transfer, the reason of "nieznany", a status set by hand. */
export function occurrenceDetail(o: Occurrence): string {
  const parts: string[] = [];
  if (o.source === 'reczny') parts.push(`ustawione ręcznie${o.note ? `: ${o.note}` : ''} (automatycznie: ${PAYMENT_LABELS[o.auto.status]})`);
  const op = o.operation;
  if (op) {
    const amount = op.amountGrosze !== undefined ? ` · ${formatPln(-op.amountGrosze)}` : '';
    const who = [op.counterpartyName, op.title].filter((x) => x).join(' · ');
    parts.push(`przelew ${dayLabel(op.bookingDate)}${amount}${who ? ` · ${who}` : ''}`);
  } else if (o.source === 'auto' && o.reason) parts.push(REASONS[o.reason]);
  else if (o.source === 'auto' && o.status === 'nieoplacone') parts.push(`brak przelewu, termin ${dayLabel(o.dueDate)}`);
  return parts.join(' · ');
}

/** The small coloured label of a period's status (the words, never colour alone). */
export function PaymentLabel({ o }: { o: Occurrence }) {
  return (
    <span className="pay-status" data-status={o.status} title={occurrenceDetail(o)}>
      {PAYMENT_LABELS[o.status]}
      {o.source === 'reczny' && <span className="pay-manual"> (ręcznie)</span>}
    </span>
  );
}

const errorText = (e: unknown, fallback: string) => (e instanceof ApiError ? (fieldIssues(e)[0]?.message ?? e.message) : fallback);

/** The cost's details: its periods (newest first) with their statuses; "…" sets a status by hand or goes back. */
export function FixedCostPeriods({ cost, me }: { cost: FixedCost; me: Me }) {
  const queryClient = useQueryClient();
  const key = [...FINANCE_KEY, 'fixed', cost.id, 'occurrences'];
  const list = useQuery({ queryKey: key, queryFn: () => api<{ occurrences: Occurrence[] }>(`/api/finance/fixed-costs/${cost.id}/occurrences`) });
  const [marking, setMarking] = useState<{ month: string; status: PaymentStatus } | null>(null);
  const [note, setNote] = useState('');
  const refresh = () => void queryClient.invalidateQueries({ queryKey: FINANCE_KEY });
  const set = useMutation({
    mutationFn: (v: { month: string; status: PaymentStatus; note: string }) => api(`/api/finance/fixed-costs/${cost.id}/occurrences/${v.month}/status`, { method: 'PUT', body: { status: v.status, note: v.note } }),
    onSuccess: () => {
      setMarking(null);
      refresh();
    },
  });
  const clear = useMutation({ mutationFn: (month: string) => api(`/api/finance/fixed-costs/${cost.id}/occurrences/${month}/status`, { method: 'DELETE' }), onSettled: refresh });
  const mayEdit = can(me, FIXED_RESOURCE, 'edit');
  const actions = (o: Occurrence): RowAction[] =>
    mayEdit
      ? [
          ...(['oplacone', 'nieoplacone', 'nieznany'] as PaymentStatus[]).map((status) => ({
            label: `Oznacz jako ${PAYMENT_LABELS[status]}`,
            onSelect: () => {
              setNote(o.source === 'reczny' ? (o.note ?? '') : '');
              setMarking({ month: o.month, status });
            },
          })),
          ...(o.source === 'reczny' ? [{ label: 'Wróć do automatycznego', onSelect: () => clear.mutate(o.month) }] : []),
        ]
      : [];
  function submit(e: FormEvent) {
    e.preventDefault();
    if (marking) set.mutate({ ...marking, note: note.trim() });
  }
  const rows = list.data?.occurrences ?? [];
  return (
    <div className="fixed-periods" aria-label={`Okresy: ${cost.name}`} role="region">
      {list.error && <p className="alert" role="alert">{errorText(list.error, 'Nie udało się wczytać okresów')}</p>}
      {list.data && rows.length === 0 && <p className="muted">Ten koszt jeszcze się nie zaczął.</p>}
      {rows.length > 0 && (
        <table className="table fin-table">
          <thead>
            <tr>
              <th scope="col">Okres</th>
              <th scope="col">Status</th>
              <th scope="col">Szczegóły</th>
              <th scope="col">
                <span className="visually-hidden">Działania</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((o) => (
              <tr key={o.month} data-month={o.month}>
                <td>{monthLabel(o.month)}</td>
                <td>
                  <PaymentLabel o={o} />
                </td>
                <td className="muted">
                  {occurrenceDetail(o)}
                  {marking?.month === o.month && (
                    <form className="fin-add" aria-label={`Oznacz jako ${PAYMENT_LABELS[marking.status]}: ${monthLabel(o.month)}`} onSubmit={submit}>
                      <label className="field">
                        <span>Notatka (opcjonalnie)</span>
                        <input value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} autoComplete="off" />
                      </label>
                      <button className="btn primary small" type="submit" disabled={set.isPending}>
                        Oznacz jako {PAYMENT_LABELS[marking.status]}
                      </button>
                      <button className="btn ghost small" type="button" onClick={() => setMarking(null)}>
                        Anuluj
                      </button>
                    </form>
                  )}
                </td>
                <td className="q-actions">
                  <RowMenu label={`Status: ${monthLabel(o.month)}`} actions={actions(o)} floating />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {(set.error || clear.error) && <p className="alert" role="alert">{errorText(set.error ?? clear.error, 'Nie udało się zmienić statusu')}</p>}
    </div>
  );
}
